import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertTriangle, Plus, Youtube, Facebook, Instagram, Linkedin, Check, AlertCircle, X, RefreshCw, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    getIntegrations,
    getOAuthUrl,
    deleteIntegration,
} from '../../api/socialMedia';
import { getSocialMediaLimit, normalizeTier } from '../../utils/subscriptionHelpers';
import { PLATFORM_COLORS, PLATFORMS } from '../../utils/socialMediaHelpers';

const SUPPORTED_PLATFORMS = ['facebook', 'youtube', 'instagram', 'linkedin'];

const PLATFORM_ICONS = {
    youtube: Youtube,
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin,
};

const PLATFORM_STATUS = {
    facebook: { available: true, note: null },
    youtube: { available: true, note: null },
    instagram: { available: false, note: 'Requires Facebook Business - Coming Soon' },
    linkedin: { available: false, note: 'Coming Soon' },
};

/**
 * PlatformConnections - Manages connected social media accounts
 * Supports multiple accounts per platform, grouped by brand
 */
const PlatformConnections = ({ subscription, currentBrandId }) => {
    const [integrations, setIntegrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [connectingPlatform, setConnectingPlatform] = useState(null);
    const [disconnectingId, setDisconnectingId] = useState(null);
    const [confirmDisconnect, setConfirmDisconnect] = useState(null);

    // Get tier and limits
    const tier = normalizeTier(subscription?.tier || subscription?.plan?.tier || subscription?.plan?.name);
    const integrationLimit = getSocialMediaLimit(tier);
    const connectedCount = integrations.length;
    const canConnectMore = connectedCount < integrationLimit;

    // Load integrations
    const loadIntegrations = useCallback(async () => {
        try {
            setError(null);
            const filters = currentBrandId ? { brand_id: currentBrandId } : {};
            const response = await getIntegrations(filters);
            // API returns { integrations: [...], total: N }
            const integrationsList = response.integrations || response.data || [];
            setIntegrations(integrationsList);
        } catch (err) {
            console.error('Failed to load integrations:', err);
            setError(err.message || 'Failed to load connected accounts');
        } finally {
            setLoading(false);
        }
    }, [currentBrandId]);

    useEffect(() => {
        loadIntegrations();
    }, [loadIntegrations]);

    // Check for OAuth success message on mount
    useEffect(() => {
        const successPlatform = sessionStorage.getItem('social_oauth_success');
        if (successPlatform) {
            sessionStorage.removeItem('social_oauth_success');
            sessionStorage.removeItem('social_oauth_account_name');
            loadIntegrations();
        }
    }, [loadIntegrations]);

    // Handle connect
    const handleConnect = async (platform) => {
        if (!canConnectMore) {
            setError(`You've reached your limit of ${integrationLimit} connected accounts. Upgrade to connect more.`);
            return;
        }

        // Check if platform is available
        const platformStatus = PLATFORM_STATUS[platform];
        if (!platformStatus?.available) {
            setError(platformStatus?.note || `${PLATFORMS[platform]?.name || platform} is not available yet.`);
            return;
        }

        // Check if a brand is selected (required by backend)
        if (!currentBrandId) {
            setError('Please create a brand first before connecting social accounts. Use the brand switcher above to create one.');
            return;
        }

        try {
            setConnectingPlatform(platform);
            setError(null);

            console.log('[PlatformConnections] handleConnect:', { platform, currentBrandId });

            // Pass brand_id (required by backend)
            const response = await getOAuthUrl(platform, currentBrandId);

            // Handle various response formats from the API
            const data = response.data || response;
            const url = data.authUrl || data.url || data.oauth_url || data.authorization_url;
            const state = data.state;

            if (!url) {
                console.error('[Social Media] No OAuth URL in response:', response);
                throw new Error('No OAuth URL received from server');
            }

            // Store state for CSRF verification
            if (state) {
                sessionStorage.setItem('social_oauth_state', state);
            }
            sessionStorage.setItem('social_oauth_platform', platform);
            if (currentBrandId) {
                sessionStorage.setItem('social_oauth_brand_id', currentBrandId);
            }

            // Redirect to OAuth provider
            window.location.href = url;
        } catch (err) {
            console.error('Failed to initiate OAuth:', err);
            setError(err.message || `Failed to connect to ${platform}`);
            setConnectingPlatform(null);
        }
    };

    // Handle disconnect
    const handleDisconnect = async (integrationId) => {
        try {
            setDisconnectingId(integrationId);
            setError(null);

            await deleteIntegration(integrationId);
            setIntegrations(prev => prev.filter(i => i.id !== integrationId));
            setConfirmDisconnect(null);
        } catch (err) {
            console.error('Failed to disconnect:', err);
            setError(err.message || 'Failed to disconnect account');
        } finally {
            setDisconnectingId(null);
        }
    };

    // Group integrations by platform
    const integrationsByPlatform = SUPPORTED_PLATFORMS.reduce((acc, platform) => {
        acc[platform] = integrations.filter(i => i.platform === platform);
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8">
            {/* Header Stats */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                            Connected Accounts
                        </h2>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                            Manage your social media platform connections
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-bold text-slate-900 dark:text-white">
                            {connectedCount} / {integrationLimit}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            accounts connected
                        </p>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(connectedCount / integrationLimit) * 100}%` }}
                        className={`h-full rounded-full ${
                            connectedCount >= integrationLimit
                                ? 'bg-amber-500'
                                : 'bg-sky-500'
                        }`}
                    />
                </div>
            </div>

            {/* Error Message */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3"
                    >
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                        </div>
                        <button
                            onClick={() => setError(null)}
                            className="text-red-500 hover:text-red-700 dark:hover:text-red-300"
                        >
                            <span className="sr-only">Dismiss</span>
                            <X className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Platform Sections */}
            <div className="space-y-8">
                {SUPPORTED_PLATFORMS.map((platform) => {
                    const Icon = PLATFORM_ICONS[platform];
                    const platformInfo = PLATFORMS[platform];
                    const colors = PLATFORM_COLORS[platform];
                    const platformIntegrations = integrationsByPlatform[platform];
                    const platformStatus = PLATFORM_STATUS[platform];
                    const isAvailable = platformStatus?.available;

                    return (
                        <div key={platform} className="space-y-4">
                            {/* Platform Header */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2.5 rounded-xl ${colors.bg} ${colors.dark.bg}`}>
                                        <Icon className={`w-5 h-5 ${colors.text} ${colors.dark.text}`} />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-slate-900 dark:text-white">
                                            {platformInfo?.name || platform}
                                        </h3>
                                        {!isAvailable && platformStatus?.note && (
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                {platformStatus.note}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Connect Button */}
                                {isAvailable && canConnectMore && (
                                    <button
                                        onClick={() => handleConnect(platform)}
                                        disabled={connectingPlatform !== null}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                            connectingPlatform === platform
                                                ? 'bg-slate-100 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                                                : `${colors.bg} ${colors.dark.bg} ${colors.text} ${colors.dark.text} hover:opacity-80`
                                        }`}
                                    >
                                        {connectingPlatform === platform ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Connecting...
                                            </>
                                        ) : (
                                            <>
                                                <Plus className="w-4 h-4" />
                                                {platformIntegrations.length > 0 ? 'Connect Another' : `Connect ${platformInfo?.name}`}
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            {/* Connected Accounts for this Platform */}
                            {platformIntegrations.length > 0 ? (
                                <div className="space-y-3">
                                    {platformIntegrations.map((integration) => (
                                        <IntegrationCard
                                            key={integration.id}
                                            integration={integration}
                                            platform={platform}
                                            colors={colors}
                                            Icon={Icon}
                                            onDisconnect={(id) => setConfirmDisconnect(id)}
                                            onRefresh={() => handleConnect(platform)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className={`p-4 rounded-xl border-2 border-dashed ${
                                    isAvailable
                                        ? 'border-slate-200 dark:border-slate-700'
                                        : 'border-slate-100 dark:border-slate-800 opacity-50'
                                }`}>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
                                        {isAvailable
                                            ? `No ${platformInfo?.name} accounts connected yet`
                                            : platformStatus?.note || 'Coming soon'
                                        }
                                    </p>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Disconnect Confirmation Modal */}
            <AnimatePresence>
                {confirmDisconnect && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
                        onClick={() => setConfirmDisconnect(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-md p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-xl"
                        >
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                                Disconnect Account?
                            </h3>
                            <p className="text-slate-600 dark:text-slate-400 mb-6">
                                This will remove the connection and you'll need to reconnect to post to this account again. Scheduled posts for this account will be cancelled.
                            </p>
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={() => setConfirmDisconnect(null)}
                                    className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleDisconnect(confirmDisconnect)}
                                    disabled={disconnectingId === confirmDisconnect}
                                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2"
                                >
                                    {disconnectingId === confirmDisconnect ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Disconnecting...
                                        </>
                                    ) : (
                                        'Disconnect'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Help Text */}
            <div className="mt-8 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                    Tips for connecting accounts
                </h4>
                <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                    <li className="flex items-start gap-2">
                        <span className="text-sky-500">•</span>
                        Make sure you're logged into the correct account before connecting
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="text-sky-500">•</span>
                        For Facebook, you'll select which Page to connect after authorization
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="text-sky-500">•</span>
                        You can connect multiple accounts from the same platform
                    </li>
                    <li className="flex items-start gap-2">
                        <span className="text-sky-500">•</span>
                        YouTube requires a channel to be set up before connecting
                    </li>
                </ul>
            </div>
        </div>
    );
};

/**
 * Integration Card - Displays a single connected account
 */
const IntegrationCard = ({
    integration,
    platform,
    colors,
    Icon,
    onDisconnect,
    onRefresh
}) => {
    const isExpired = integration?.status === 'expired';
    const needsReconnect = integration?.status === 'needs_reconnect';

    const getStatusBadge = () => {
        if (isExpired || needsReconnect) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <AlertCircle className="w-3 h-3" />
                    Reconnect
                </span>
            );
        }

        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                <Check className="w-3 h-3" />
                Connected
            </span>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`p-4 rounded-xl border transition-all ${colors.border} ${colors.dark.border} bg-white dark:bg-slate-800/50`}
        >
            <div className="flex items-center gap-4">
                {/* Account Avatar */}
                <div className="relative flex-shrink-0">
                    {integration.accountPicture || integration.avatar_url ? (
                        <img
                            src={integration.accountPicture || integration.avatar_url}
                            alt={integration.accountName || integration.account_name}
                            className="w-12 h-12 rounded-full border-2 border-white dark:border-slate-700 shadow"
                        />
                    ) : (
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colors.bg} ${colors.dark.bg}`}>
                            <Icon className={`w-6 h-6 ${colors.text} ${colors.dark.text}`} />
                        </div>
                    )}
                </div>

                {/* Account Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-slate-900 dark:text-white truncate">
                            {integration.accountName || integration.display_name || integration.account_name}
                        </p>
                        {getStatusBadge()}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                        {integration.accountUsername && (
                            <span>@{integration.accountUsername}</span>
                        )}
                        {(integration.followers_count !== undefined || integration.fan_count !== undefined) && (
                            <span className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" />
                                {(integration.followers_count || integration.fan_count || 0).toLocaleString()} followers
                            </span>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                    {(isExpired || needsReconnect) && (
                        <button
                            onClick={onRefresh}
                            className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                            title="Reconnect"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </button>
                    )}
                    <button
                        onClick={() => onDisconnect(integration.id)}
                        className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        title="Disconnect"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

export default PlatformConnections;
